import { FileUp, X } from "lucide-react";
import { useRef, useState, type DragEvent } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "./Button";
import { cn } from "./cn";

export interface FileDropProps {
  /** Comma-separated `accept` list, e.g. `".xlsx,.csv"`. */
  accept?: string;
  multiple?: boolean;
  /** Called with every accepted file; replaces the previous set unless `multiple`. */
  onFiles: (files: File[]) => void;
  files?: File[];
  onRemove?: (file: File) => void;
  disabled?: boolean;
  hint?: string;
}

/**
 * The `Upload` replacement: a drop zone plus a picker button.
 *
 * Vaadin's component streamed to the server as part of the session; here the
 * File objects stay in the page and each screen decides how to send them —
 * usually as an `application/octet-stream` body, which is what every import
 * endpoint in the estate consumes.
 */
export function FileDrop({
  accept,
  multiple = false,
  onFiles,
  files = [],
  onRemove,
  disabled,
  hint,
}: FileDropProps) {
  const { t } = useTranslation();
  const input = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);

  const accepted = (list: FileList | null) => {
    if (!list || list.length === 0) return;
    onFiles(Array.from(list));
    // Clearing lets the same file be picked twice in a row, which otherwise
    // fires no change event at all.
    if (input.current) input.current.value = "";
  };

  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setOver(false);
    if (!disabled) accepted(event.dataTransfer.files);
  };

  return (
    <div className="flex flex-col gap-2">
      <div
        onDragOver={(event) => {
          event.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={onDrop}
        className={cn(
          "flex flex-col items-center gap-2 rounded-lg border border-dashed px-4 py-6 text-center",
          over ? "border-accent bg-accent-soft" : "border-line bg-surface-sunk",
          disabled && "pointer-events-none opacity-60",
        )}
      >
        <FileUp className="size-5 text-ink-faint" />
        <p className="text-[13px] text-ink-muted">{hint ?? t("form.field.drop-label", "Drop a file here")}</p>
        <Button
          size="sm"
          variant="primary"
          onClick={() => input.current?.click()}
          icon={<FileUp className="size-3.5" />}
        >
          {t("button.upload", "Upload")}
        </Button>
        <input
          ref={input}
          type="file"
          hidden
          {...(accept ? { accept } : {})}
          multiple={multiple}
          onChange={(event) => accepted(event.target.files)}
        />
      </div>

      {files.length > 0 && (
        <ul className="flex flex-col gap-1">
          {files.map((file) => (
            <li
              key={`${file.name}-${file.size}-${file.lastModified}`}
              className="flex items-center gap-2 rounded border border-line bg-surface px-2 py-1 text-[13px] text-ink"
            >
              <span className="min-w-0 flex-1 truncate">{file.name}</span>
              <span className="shrink-0 text-[12px] text-ink-faint">{formatSize(file.size)}</span>
              {onRemove && (
                <button
                  type="button"
                  onClick={() => onRemove(file)}
                  aria-label={t("button.delete", "Delete")}
                  className="shrink-0 rounded p-0.5 text-ink-faint hover:bg-surface-sunk hover:text-risk"
                >
                  <X className="size-3.5" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
